'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { ProductCardV2Brand } from '@packages/modules/src/ProductCardV2/ProductCardV2.Brand';
import { ProductCardGridItem } from '@packages/modules/src/ProductCardV2/ProductCardV2.GridItem.client';
import { ProductCardV2Image } from '@packages/modules/src/ProductCardV2/ProductCardV2.Image';
import { ProductCardV2Name } from '@packages/modules/src/ProductCardV2/ProductCardV2.Name';
import { ProductCardV2Price } from '@packages/modules/src/ProductCardV2/ProductCardV2.Price.client';
import { ProductCardV2WishlistButton } from '@packages/modules/src/ProductCardV2/ProductCardV2.WishlistButton';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinProductClick } from '@packages/tracking/src/types/events';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { ProductCardV2EfficiencyFlags } from '@packages/modules/src/ProductCardV2/ProductCardV2.EfficiencyFlags';
import { getFormattedProduct } from './helper/getFormattedProduct';
import { SeenObserver } from './SeenObserver.client';

/* GraphQL */ `
  fragment RecoProductCard on ProductRecommendation {
    product {
      akl
      name(type: NO_BRAND)
      orderNumber
      promotion
      sku
      size
      link
      brand {
        name
      }
      downloads(type: PRODUCT_DATASHEET) {
        link
      }
      media {
        type
        url
      }
      powerEfficiencyFlags {
        color
        hasOldLabel
        level
        link
        text
        dataSheetKeys
      }
      price {
        amount
        currency
        isStartPrice
        saving {
          type
          value
        }
        uvp
        unit
        value
        valueOld
        valueUnit
      }
      tireEfficiency {
        flags {
          level
          type
        }
        link
      }
    }
    tracking {
      adId
      token
      trackingUrls {
        url
        purpose
      }
      logic
    }
  }
`;

export type RecoProductCardProps = {
  data: FragmentType<typeof RecoProductCardFragmentDoc>;
  /**
   * Recommendation type abbreviation, e.g. "prudsys_dv"
   */
  rdeTypeAbbr?: string;
  /**
   * Position of product in the recommendation list, starting with 1.
   */
  position: number;
  /**
   * Event handler for when a product is clicked.
   * This function is called when a user clicks a product element.
   */
  onClick?: (akl: string, sku: string) => void;
  /**
   * Called with the sku of the product, once the card has been fully visible in the viewport for
   * the first time.
   *
   * Only pass this when the consumer actually needs seen tracking: without it no
   * `IntersectionObserver` is created for the card at all.
   */
  onFirstBecameVisible?: (sku: string) => void;
  className?: string;
  /**
   * Category ID of the recommended products.
   */
  categoryId?: string;
};

export const RecoProductCard = ({
  data,
  rdeTypeAbbr,
  position,
  className,
  onClick,
  onFirstBecameVisible,
  categoryId,
}: RecoProductCardProps) => {
  const { product, tracking } = unmask<typeof RecoProductCardFragmentDoc>(data);
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();
  const cardRef = useRef<HTMLLIElement>(null);

  if (!product) return null;

  const {
    imageUrl,
    price,
    productId,
    productLink,
    energyEfficiencyFlags,
    // New flags are disabled until EEK flag can be assigned to corresponding datasheet
    // tireEfficiencyFlags,
    legacyEfficiencyFlags,
  } = getFormattedProduct(product, formatMessage, categoryId);

  return (
    <ProductCardGridItem
      className={`bg-color-background-base mb-0 [&>:last-child]:mb-0.5 lg:[&>:last-child]:mb-1 ${className}`}
      title={product?.name}
      id={productId}
      ref={cardRef}
      onClick={() => {
        dispatchGTMEvent<GTMEventGlycerinProductClick>({
          event: 'ProductClick',
          ProductClickData: {
            category: 'product-click',
            productId: product.akl,
            articleId: product.orderNumber,
            detail: `${product.brand?.name} ${product.name}`,
            skuId: product.sku,
            recoId: rdeTypeAbbr,
            listPosition: position,
            custom: {
              trackingToken: tracking.token,
              ...(tracking?.adId && { adId: tracking.adId }),
            },
          },
        });
        onClick?.(product.akl, product.sku);
      }}
    >
      {/* rendered conditionally, so cards without seen tracking never create an IntersectionObserver */}
      {onFirstBecameVisible && (
        <SeenObserver
          cardRef={cardRef}
          sku={product.sku}
          onFirstBecameVisible={onFirstBecameVisible}
        />
      )}
      <ProductCardV2Name id={productId} href={productLink} className="mx-1 line-clamp-2">
        {product.name}
      </ProductCardV2Name>
      <ProductCardV2Brand id={productId} className="mx-1 mt-1 line-clamp-1">
        {product.brand?.name}
      </ProductCardV2Brand>

      <ProductCardV2Image
        src={imageUrl}
        alt=""
        className="bg-color-background-base aspect-square p-0.5"
        imageClassName="rounded-t-sm"
      />
      <ProductCardV2WishlistButton
        size="medium"
        productName={product.name}
        sku={product.sku}
        wishListTrackingProps={{
          products: [
            {
              productId: product.akl,
              articleId: product.orderNumber + product.promotion,
              currency: product.price.currency,
              displayedPrice: product.price.value,
              originalPrice: product.price.valueOld ?? undefined,
              listPosition: position,
              origin: 'Reco',
            },
          ],
          custom: {
            size: product.size,
            productName: product.name,
            orderNumber: product.orderNumber,
            promotion: product.promotion ?? '',
          },
        }}
      />
      <ProductCardV2Price id={productId} price={price} className="group price-size-small mx-1" />
      <ProductCardV2EfficiencyFlags
        energyFlags={energyEfficiencyFlags}
        legacyFlags={legacyEfficiencyFlags}
        // Tire flags are rendered through legacyFlags until the EEK flag can be assigned to its corresponding datasheet, that is why tireFlags is not passed here
        // tireFlags={tireEfficiencyFlags}
      />
    </ProductCardGridItem>
  );
};
